/**
 * SymbolOverviewDrawer — a unified "size up this symbol" slide-over: the latest
 * captured OverView score/zone per timeframe, recent forward outcomes, and quick
 * actions (open chart, set price alert). Reuses the Edge snapshot (/edge/symbol)
 * so it needs no new backend; degrades gracefully when Edge hasn't captured yet.
 */
import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Chip, Divider, Button, CircularProgress, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { edgeService } from '../../services/edge.service';
import PriceAlertDialog from '../alerts/PriceAlertDialog';
import AiVerdictChip from '../ai/AiVerdictChip';
import type { AiAssessment } from '../../services/ai.service';

interface Props { open: boolean; symbol: string; exchange?: string; onClose: () => void; }

const ZONE_COLOR: Record<string, string> = {
  strongBuy: '#1b8f6a', probableBuy: '#26a69a', neutral: '#90a4ae', probableShort: '#ef9a9a', strongShort: '#ef5350',
};
const zoneLabel = (z?: string) => z ? z.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim() : '—';

const SymbolOverviewDrawer: React.FC<Props> = ({ open, symbol, exchange, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [ai, setAi] = useState<AiAssessment | null>(null);

  useEffect(() => {
    if (!open || !symbol) return;
    let cancelled = false;
    setLoading(true); setData(null); setAi(null);
    edgeService.getSymbol(symbol).then((r: any) => { if (!cancelled) setData(r.data?.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, symbol]);

  const snap = data?.latest;
  const tfs = snap?.features?.tfs || {};
  const tfOrder = ['tf60', 'tf240', 'tfD', 'tfW'];
  const tfLabel: Record<string, string> = { tf60: '1h', tf240: '4h', tfD: '1D', tfW: '1W' };
  const outcomes: any[] = data?.outcomes || [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, p: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{symbol}</Typography>
        <Typography variant="caption" color="text.secondary">{exchange}</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Quick actions */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button size="small" variant="outlined" startIcon={<CandlestickChartIcon />}
          onClick={() => { navigate(`/charts/${encodeURIComponent(symbol)}?exchange=${encodeURIComponent(exchange || '')}`); onClose(); }}>
          Chart
        </Button>
        <Button size="small" variant="outlined" startIcon={<AddAlertIcon />} onClick={() => setAlertOpen(true)}>
          Price alert
        </Button>
      </Box>

      {/* AI second opinion. Deliberately asked ON DEMAND rather than on open: every ask is a
          billed model call, and opening a drawer to glance at a score should not spend money. */}
      {!!exchange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>AI view</Typography>
          <AiVerdictChip
            assessment={ai}
            symbol={symbol}
            exchange={exchange}
            timeframe={1440}
            askSource="symbol overview drawer"
            askLabel="Ask AI"
            onAssessed={setAi}
          />
          <Tooltip arrow title="Asks Claude to assess this symbol on daily evidence and say whether it is worth acting on. It gives an opinion only — it never sets a stop, a size or an order. Hover the result for the full reasoning.">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={22} /></Box>
      ) : !snap ? (
        <Typography variant="body2" color="text.secondary">
          No Edge AI snapshot for this symbol yet. Enable Edge AI and add this symbol to your universe to see its
          OverView scores and forward-move history here. You can still open its chart or set a price alert above.
        </Typography>
      ) : (
        <>
          {snap.spotClose != null && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Last close <b>{Number(snap.spotClose).toLocaleString('en-IN')}</b>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                captured {new Date(snap.captureTime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
              </Typography>
            </Typography>
          )}
          {snap.regimeTag && <Chip size="small" label={`Regime: ${snap.regimeTag}`} sx={{ mb: 1.5 }} />}

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>OverView score by timeframe</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, mb: 2 }}>
            {tfOrder.filter(k => tfs[k]).map(k => {
              const t = tfs[k];
              return (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', width: 28, fontWeight: 700 }}>{tfLabel[k]}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', width: 44, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{t.score == null ? '—' : t.score}</Typography>
                  <Chip size="small" label={zoneLabel(t.zone)} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: ZONE_COLOR[t.zone] || '#90a4ae', color: '#fff' }} />
                </Box>
              );
            })}
          </Box>

          {outcomes.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Recent forward moves</Typography>
                <Tooltip arrow title="How this symbol actually moved AFTER past Edge AI snapshots: the realised % price change over the next day / next week, and the outcome label (up / down / flat). It's a track record of what happened historically, not a prediction.">
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.3 }}>
                Realised move over the next day / week after each past snapshot (history, not a forecast).
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                {outcomes.slice(0, 8).map((o: any, i: number) => {
                  const ret = Number(o.fwdReturnPct) || 0;
                  return (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem' }}>
                      <span style={{ color: '#90a4ae' }}>{(o.horizon || '').replace('_', ' ').toLowerCase()} · {(o.resolvedAt || '').slice(0, 10)}</span>
                      <span style={{ fontWeight: 700, color: ret >= 0 ? '#26a69a' : '#ef5350' }}>{ret >= 0 ? '+' : ''}{ret.toFixed(2)}% · {o.label?.replace('_', ' ')}</span>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </>
      )}

      <PriceAlertDialog open={alertOpen} onClose={() => setAlertOpen(false)} initialSymbol={symbol} initialExchange={exchange}
        initialPrice={snap?.spotClose ? Number(snap.spotClose) : undefined} />
    </Drawer>
  );
};

export default SymbolOverviewDrawer;
